import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { VideoStateTool } from './VideoStateTool';

import { setSceneDurationUserOverride } from 'js/src/data/user-overrides';
import { checkpointVideo } from 'js/src/data/video-data';
import { isVoiceDrivenSceneGroup, saveRawLuminary } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { calculateAndSetRecommendedSceneDurationOperation } from 'js/src/luminary/luminary-operations';

export const legacySetSceneDurationSchema = z.object({
    scene_index: z.number().describe('The scene index (0, 1, 2, etc.)'),
    mode: z
        .enum(['auto', 'manual'])
        .describe(
            'Use "auto" to calculate duration based on scene text (recommended), or "manual" to set a specific duration',
        ),
    duration: z
        .number()
        .min(1)
        .optional()
        .describe(
            'The duration in milliseconds (required when mode is "manual", ignored when mode is "auto")',
        ),
});
export type SetSceneDurationArgs = z.infer<typeof legacySetSceneDurationSchema>;

export class LegacySetSceneDurationTool extends VideoStateTool<
    typeof legacySetSceneDurationSchema
> {
    protected override readonly schema = legacySetSceneDurationSchema;
    protected override readonly toolName = TOOL_NAMES.setSceneDuration;
    protected override readonly toolDescription =
        'Set the duration of a specific scene. Use mode "auto" to automatically calculate the optimal duration based on the text content, or mode "manual" to set a specific duration in milliseconds.';

    public override getToolDisplayName(): string {
        return 'Setting scene duration';
    }

    public get statusIcon(): string {
        return 'timer';
    }

    public async execute(args: SetSceneDurationArgs, context: ToolExecutionContext) {
        const { scene_index, duration, mode = 'manual' } = args;
        const { parentActionGroup } = context;

        try {
            if (scene_index >= this.luminary.scenes.length) {
                return `Error: scene_index ${scene_index} is out of bounds for the current video`;
            }

            if (scene_index < 0) {
                return `Error: scene_index ${scene_index} must be non-negative`;
            }

            const scene = this.luminary.scenes[scene_index];
            if (!scene) {
                return `Error: No scene found at index ${scene_index}`;
            }

            // Check if the scene is voice-driven (connected to transcript)
            const sceneGroup = scene.sceneGroup;
            if (!sceneGroup) {
                return `Error: No scene group found for scene at index ${scene_index}`;
            }
            if (isVoiceDrivenSceneGroup(sceneGroup)) {
                return `Error: Cannot edit duration for voice-driven scenes. Scene ${scene_index} is connected to a transcript (has media or voiceover asset).`;
            }

            // Check if the scene is the branding scene (cannot be edited)
            if (scene.sceneCategory === VideoData.CATEGORY_BRAND) {
                return `Error: Cannot edit duration for branding scenes. Scene ${scene_index} is a branding scene.`;
            }

            if (mode === 'auto') {
                const previousDuration = scene.duration;

                // Returning to auto means clearing any existing override first —
                // otherwise the recommended-duration operation bails out early on an
                // overridden scene and this would be a no-op.
                this.dispatch(
                    setSceneDurationUserOverride({
                        sceneId: scene.id,
                        isAutomatic: true,
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );

                const { calculateAndSetRecommendedSceneDuration } = makeOperationsCallable(
                    {
                        calculateAndSetRecommendedSceneDurationOperation,
                    },
                    this.luminary,
                    this.dispatch,
                    () => this.getState(),
                );

                calculateAndSetRecommendedSceneDuration(scene.id, false, parentActionGroup);

                const newDuration = scene.duration;
                return `Success: Automatically set duration for scene ${scene_index} to ${newDuration} milliseconds (was ${previousDuration}ms).`;
            }

            // Manual mode
            if (duration === undefined || duration <= 0) {
                return 'Error: duration must be a positive number when using manual mode';
            }

            try {
                scene.duration = duration;

                // Save changes to enable undo/redo functionality
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );

                // A deliberately-set duration must stick: mark it overridden so later
                // text edits don't auto-resize it back.
                this.dispatch(
                    setSceneDurationUserOverride({
                        sceneId: scene.id,
                        isAutomatic: false,
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );

                return `Success: Set duration for scene ${scene_index} to ${duration} milliseconds.`;
            } catch (error) {
                return `Error: Failed to set duration for scene ${scene_index}`;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting scene duration: ${errorMessage}`;
        }
    }
}
